import React, { useEffect, useState, useCallback } from "react";
import { Button, Col, Row, Alert, Spinner } from "reactstrap";
import PropTypes from "prop-types";
import XplorPaySDK from "./XplorPaySDK";
import XPLORPAY_CONFIG from "./XplorPayConfig";
import { saveXplorPayCardToken } from "api/AutopayApi";
import { failAlert, successAlertCustom } from "utils/alertUtils";

/**
 * Component to save a card for AutoPay using XplorPay SDK
 * Gets JWT token from hosted form, sends to backend to create vault token
 */
const XplorPaySaveCard = ({
  accountID,
  customerID,
  onSuccess,
  onError,
  toggle,
}) => {
  const [loading, setLoading] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const initSDK = async () => {
      try {
        await XplorPaySDK.initForCard();
        if (mounted) setSdkReady(true);
      } catch (err) {
        if (mounted) setError(err.message);
      }
    };
    initSDK();
    return () => {
      mounted = false;
      XplorPaySDK.cleanup();
    };
  }, []);

  const handleSaveCard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get token from SDK
      const { jwt, lastFour, cardType, expDate } = await XplorPaySDK.getPaymentToken();

      // Send to backend to create vault token
      const response = await saveXplorPayCardToken({
        jwt,
        lastFour,
        cardType,
        expDate,
        accountId: accountID,
        dmsCustomerId: customerID,
      });

      if (response.data?.content?.success) {
        successAlertCustom("Card saved successfully!");
        if (onSuccess) onSuccess(response.data.content);
        if (toggle) toggle();
      } else {
        throw new Error(response.data?.content?.errorMessage || response.data?.message || "Failed to save card");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to save card";
      setError(errorMessage);
      failAlert(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  }, [accountID, customerID, onSuccess, onError, toggle]);

  return (
    <div className="xplorpay-save-card">
      <Row className="mb-3">
        <Col>
          <h5>Add Credit/Debit Card</h5>
          <p className="text-muted">Enter your card details to save for AutoPay</p>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <Alert color="danger" toggle={() => setError(null)}>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {!sdkReady && !error && (
        <div className="text-center py-4">
          <Spinner color="primary" />
          <p className="mt-2">Loading secure form...</p>
        </div>
      )}

      <Row className="mb-3">
        <Col>
          <div
            id={XPLORPAY_CONFIG.paymentFormId}
            style={{
              minHeight: sdkReady ? "200px" : "0",
              border: sdkReady ? "1px solid #ddd" : "none",
              borderRadius: "4px",
              padding: sdkReady ? "15px" : "0",
              backgroundColor: "#f9f9f9",
            }}
          />
        </Col>
      </Row>

      {sdkReady && (
        <Row className="mt-3">
          <Col className="text-center">
            <Button color="primary" onClick={handleSaveCard} disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" /> Saving...
                </>
              ) : (
                "Save Card"
              )}
            </Button>
          </Col>
        </Row>
      )}

      <Row className="mt-3">
        <Col className="text-center">
          <small className="text-muted">
            <i className="fa fa-lock mr-1"></i>
            Card data is encrypted and securely stored by XplorPay.
          </small>
        </Col>
      </Row>
    </div>
  );
};

XplorPaySaveCard.propTypes = {
  accountID: PropTypes.number.isRequired,
  customerID: PropTypes.number.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  toggle: PropTypes.func,
};

export default XplorPaySaveCard;
